import { faEdit, faExclamationCircle, faPlus, faTrashAlt } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Loading from '@ssg/common/Components/Loading';
import Table from '@ssg/common/Components/Table';
import TextButton from '@ssg/common/Components/TextButton';
import {
	DebitorDelayScalepointConnection,
	GetDebitorDelayScalepointConnectionsDocument,
	GetDebitorDelayScalepointConnectionsQuery,
	GetDebitorDelayScalepointConnectionsQueryVariables,
	useDeleteDebitorDelayScalepointConnectionMutation,
	useGetDebitorDelayScalepointConnectionsQuery,
} from '@ssg/common/GraphQL/indexV2';
import React from 'react';
import DebitorScalepointCreate from './DebitorScalepointCreate';
import DebitorScalepointEdit from './DebitorScalepointEdit';
import Modal, { ModalSize } from '@ssg/common/Components/Modal';
import Alert from '@ssg/common/Components/Alert';
import Button from '@ssg/common/Components/Button';
import { useTranslation } from 'react-i18next';

export const getDebitorName = (debitor: Pick<DebitorDelayScalepointConnection['debitor'], 'debitorId' | 'company'>): string => {
	return `${debitor.debitorId} - ${debitor.company}`;
};

const DebitorScalepointSetup: React.FC = (): React.ReactElement => {
	const { t } = useTranslation();
	const { data, loading } = useGetDebitorDelayScalepointConnectionsQuery();
	const debitorDelayScalepointConnections = React.useMemo(() => data?.debitorDelayScalepointConnections ?? [], [data]);

	const [createDebitorScalepoint, setCreateDebitorScalepoint] = React.useState(false);
	const [editDebitorScalepoint, setEditDebitorScalepoint] = React.useState(false);
	const [deleteDebitorScalepoint, setDeleteDebitorScalepoint] = React.useState(false);
	const [debitorScalepoint, setDebitorScalepoint] = React.useState<GetDebitorDelayScalepointConnectionsQuery['debitorDelayScalepointConnections'][0]>();

	const [deleteDebitorScalepointMutation, { loading: loadingDelete }] = useDeleteDebitorDelayScalepointConnectionMutation();

	const submitDelete = async (id: string) => {
		await deleteDebitorScalepointMutation({
			variables: { id },
			update: (cache, { data }): void => {
				if (typeof data === 'undefined' || data === null) {
					return;
				}

				const cachedRequest = cache.readQuery<GetDebitorDelayScalepointConnectionsQuery, GetDebitorDelayScalepointConnectionsQueryVariables>({
					query: GetDebitorDelayScalepointConnectionsDocument,
				});
				if (cachedRequest === null || cachedRequest.debitorDelayScalepointConnections === null) {
					return;
				}

				cache.writeQuery<GetDebitorDelayScalepointConnectionsQuery>({
					query: GetDebitorDelayScalepointConnectionsDocument,
					data: {
						debitorDelayScalepointConnections: cachedRequest.debitorDelayScalepointConnections.filter(dds => dds.id !== id),
					},
				});
			},
		});
		setDeleteDebitorScalepoint(false);
		setDebitorScalepoint(undefined);
	};

	return (
		<div>
			<header className="flex">
				<div className="w-1/2">
					{/* <DebitorFilters setFilterTerm={setSearchTerm} debitorDropdownItems={debitorDropdownItems} setDebitorType={setDebitorType} debitorType={debitorType} /> */}
				</div>
				<div className="flex w-1/2 justify-end">
					<TextButton icon={faPlus} text="debitor.create" onClick={() => setCreateDebitorScalepoint(true)} />
				</div>
			</header>

			<div className="text-blue relative bg-white pb-1">
				{loading ? (
					<div className="h-40">
						<Loading />
					</div>
				) : (
					<Table
						data={debitorDelayScalepointConnections ?? []}
						noDataFoundText="debitor.noDebitors"
						columns={[
							{
								label: 'debitor.overviewTitle',
								selectFn: c => <p className="py-1">{getDebitorName(c.debitor)}</p>,
								sortFn: (a, b) => getDebitorName(a.debitor).localeCompare(getDebitorName(b.debitor)),
							},
							{
								label: 'questionnaire.damageCauses',
								selectFn: c => <p className="py-1">{c.damageCauses.length > 0 ? c.damageCauses.map(dc => `${dc.category.name} - ${dc.name}`).join(', ') : 'ALLE'}</p>,
							},
							{
								label: 'common.edit',
								classNameTh: 'text-right',
								selectFn: c => (
									<div className="flex content-start justify-end text-right">
										<FontAwesomeIcon
											icon={faEdit}
											size="lg"
											onClick={() => {
												setDebitorScalepoint(c);
												setEditDebitorScalepoint(true);
											}}
											className="cursor-pointer"
										/>
									</div>
								),
							},
							{
								label: 'common.delete',
								classNameTh: 'text-right',
								selectFn: c => (
									<div className="text-red flex content-start justify-end text-right">
										<FontAwesomeIcon
											icon={faTrashAlt}
											size="lg"
											onClick={() => {
												setDebitorScalepoint(c);
												setDeleteDebitorScalepoint(true);
											}}
											className="cursor-pointer"
										/>
									</div>
								),
							},
						]}
						keySelector={c => c.id}
					/>
				)}
			</div>
			{createDebitorScalepoint && <DebitorScalepointCreate close={() => setCreateDebitorScalepoint(false)} />}
			{editDebitorScalepoint && debitorScalepoint && (
				<DebitorScalepointEdit
					close={() => {
						setEditDebitorScalepoint(false);
						setDebitorScalepoint(undefined);
					}}
					debitorScalepointConnection={debitorScalepoint}
				/>
			)}

			{deleteDebitorScalepoint && debitorScalepoint && (
				<Modal
					size={ModalSize.MEDIUM}
					close={() => {
						setDeleteDebitorScalepoint(false);
						setDebitorScalepoint(undefined);
					}}
					title={t('debitor.delete', {
						debitor: getDebitorName(debitorScalepoint.debitor),
					})}
					visible
					body={
						<div>
							<Alert danger text="invoices.deleteWarning" icon={faExclamationCircle} />
							<div className="flex justify-between">
								<Button danger loading={loadingDelete} text="common.delete" className="mt-3" onClick={() => submitDelete(debitorScalepoint.id)} />
								<Button
									secondary
									text="common.cancel"
									className="mt-3"
									onClick={() => {
										setDeleteDebitorScalepoint(false);
										setDebitorScalepoint(undefined);
									}}
								/>
							</div>
						</div>
					}
				/>
			)}
		</div>
	);
};

export default DebitorScalepointSetup;

